import { isEqual } from '@travelata-libs/utils';
import { formatBudgetFilterLabel } from './budgetFilter';
import { EMIRATES_FILTER_GROUP_ID } from './emiratesFilter';
import { getPrepaymentsFilterOptions } from './prepaymentsFilter';
import { RESORTS_FILTER_GROUP_ID } from './resortsFilter';
import { DIRECT_FLIGHT_FILTER_GROUP_ID, isSearchFilterGroup, isSearchFilterOptionSelected } from './searchFilters';
import { SERP_FILTER_GROUPS } from '../config/filters';
import {
  FilterGroupId,
  type FiltersModel,
  type SearchCriteriaFilters,
  type SelectedFilter,
  type SerpBudgetFilterGroup,
  type SerpCriteriaFilterGroup,
  type SerpDynamicFilterOptions,
  type SerpFilterOption,
  type SerpOperatorsFilterGroup,
  type SerpOptionsFilterGroup,
  type SerpPrepaymentsFilterGroup,
} from '../model/types';

export function createEmptyFiltersModel(): FiltersModel {
  return {
    isExclusive: false,
    budget: { min: 0, max: 0 },
    beachLine: [],
    beachType: [],
    beachOwner: false,
    bonusMiles: false,
    nearTheCenter: false,
    rating: null,
    prepayments: [],
    facilities: [],
    waterActivities: [],
    childServices: [],
    renovation: [],
    distanceLift: [],
    restType: null,
    operators: [],
  };
}

function findGroup(groupId: FilterGroupId): SerpOptionsFilterGroup | SerpBudgetFilterGroup | undefined {
  for (const group of SERP_FILTER_GROUPS) {
    if (!isSearchFilterGroup(group) && group.id === groupId) return group;
  }

  return undefined;
}

function withoutKey(keys: readonly string[], key: string): string[] {
  return keys.filter((selectedKey) => selectedKey !== key);
}

function toggleKey(keys: readonly string[], key: string): string[] {
  return keys.includes(key) ? withoutKey(keys, key) : [...keys, key];
}

function normalizeBudgetBound(value: number): number {
  return Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
}

export function isBudgetSelected(budget: FiltersModel['budget']): boolean {
  return budget.min > 0 || budget.max > 0;
}

const NO_DYNAMIC_OPTIONS: SerpDynamicFilterOptions = {
  prepayments: [],
  operators: [],
  resorts: [],
  directFlightAvailable: false,
  emiratesAvailable: false,
};

export function isDynamicFilterGroup(
  group: SerpOptionsFilterGroup,
): group is SerpPrepaymentsFilterGroup | SerpOperatorsFilterGroup {
  return group.id === FilterGroupId.Prepayments || group.id === FilterGroupId.Operators;
}

function getSelectedDynamicOptions(
  group: SerpOptionsFilterGroup,
  filtersModel: FiltersModel,
  dynamicOptions: SerpDynamicFilterOptions,
): readonly SerpFilterOption[] {
  if (group.id === FilterGroupId.Prepayments) return getPrepaymentsFilterOptions(filtersModel.prepayments);

  if (group.id === FilterGroupId.Operators) {
    return filtersModel.operators.flatMap((key) => {
      const option = dynamicOptions.operators.find((candidate) => candidate.key === key);

      return option ? [option] : [];
    });
  }

  return [];
}

export function getFilterGroupOptions(
  group: SerpOptionsFilterGroup | SerpCriteriaFilterGroup,
  dynamicOptions: SerpDynamicFilterOptions,
): readonly SerpFilterOption[] {
  if (group.id === RESORTS_FILTER_GROUP_ID) return dynamicOptions.resorts;

  if (group.id === DIRECT_FLIGHT_FILTER_GROUP_ID && !dynamicOptions.directFlightAvailable) return [];

  if (group.id === EMIRATES_FILTER_GROUP_ID && !dynamicOptions.emiratesAvailable) return [];

  if (group.id === FilterGroupId.Prepayments) return dynamicOptions.prepayments;

  if (group.id === FilterGroupId.Operators) return dynamicOptions.operators;

  return group.control === 'checkbox' ? [{ key: group.id, label: group.title }] : group.options;
}

export function isFilterOptionSelected(
  filtersModel: FiltersModel,
  group: SerpOptionsFilterGroup,
  key: string,
): boolean {
  switch (group.control) {
    case 'checkbox': {
      return filtersModel[group.id];
    }
    case 'chips':
    case 'checkboxes': {
      const selectedKeys: readonly string[] = filtersModel[group.id];

      return selectedKeys.includes(key);
    }
    case 'radio': {
      return filtersModel[group.id] === key;
    }
  }
}

export function getSelectedFilterKeys(filtersModel: FiltersModel, group: SerpOptionsFilterGroup): readonly string[] {
  switch (group.control) {
    case 'checkbox': {
      return filtersModel[group.id] ? [group.id] : [];
    }
    case 'chips':
    case 'checkboxes': {
      return filtersModel[group.id];
    }
    case 'radio': {
      const key = filtersModel[group.id];

      return key ? [key] : [];
    }
  }
}

export function isFilterModelSelected(filtersModel: FiltersModel | null): boolean {
  if (!filtersModel) return false;
  return SERP_FILTER_GROUPS.some((group) => {
    if (isSearchFilterGroup(group)) return false;
    if (group.control === 'budget') return isBudgetSelected(filtersModel.budget);

    return getSelectedFilterKeys(filtersModel, group).length > 0;
  });
}

export function getSelectedFilters(
  filtersModel: FiltersModel,
  criteriaFilters: SearchCriteriaFilters,
  dynamicOptions: SerpDynamicFilterOptions = NO_DYNAMIC_OPTIONS,
): SelectedFilter[] {
  const selectedFilters: SelectedFilter[] = [];

  for (const group of SERP_FILTER_GROUPS) {
    if (group.control === 'budget') {
      if (isBudgetSelected(filtersModel.budget)) {
        selectedFilters.push({ groupId: group.id, key: group.id, label: formatBudgetFilterLabel(filtersModel.budget) });
      }

      continue;
    }

    if (!isSearchFilterGroup(group) && isDynamicFilterGroup(group)) {
      for (const option of getSelectedDynamicOptions(group, filtersModel, dynamicOptions)) {
        selectedFilters.push({ groupId: group.id, key: option.key, label: option.label });
      }

      continue;
    }

    for (const option of getFilterGroupOptions(group, dynamicOptions)) {
      const selected = isSearchFilterGroup(group)
        ? isSearchFilterOptionSelected(criteriaFilters, group.id, option.key)
        : isFilterOptionSelected(filtersModel, group, option.key);

      if (selected) {
        selectedFilters.push({ groupId: group.id, key: option.key, label: option.label });
      }
    }
  }

  return selectedFilters;
}

export function countSelectedFilters(
  filtersModel: FiltersModel,
  criteriaFilters: SearchCriteriaFilters,
  dynamicOptions: SerpDynamicFilterOptions = NO_DYNAMIC_OPTIONS,
): number {
  return getSelectedFilters(filtersModel, criteriaFilters, dynamicOptions).length;
}

export function toggleFilter(filtersModel: FiltersModel, groupId: FilterGroupId, key: string): FiltersModel {
  const group = findGroup(groupId);

  switch (group?.control) {
    case 'checkbox': {
      return { ...filtersModel, [group.id]: !filtersModel[group.id] };
    }
    case 'chips':
    case 'checkboxes': {
      return { ...filtersModel, [group.id]: toggleKey(filtersModel[group.id], key) };
    }
    case 'radio': {
      return { ...filtersModel, [group.id]: key };
    }
    default: {
      return filtersModel;
    }
  }
}

export function setBudgetFilter(filtersModel: FiltersModel, budget: FiltersModel['budget']): FiltersModel {
  const min = normalizeBudgetBound(budget.min);
  const max = normalizeBudgetBound(budget.max);

  if (min > 0 && max > 0 && min > max) return { ...filtersModel, budget: { min: max, max: min } };

  return { ...filtersModel, budget: { min, max } };
}

export function clearFilterGroup(filtersModel: FiltersModel, groupId: FilterGroupId): FiltersModel {
  return { ...filtersModel, [groupId]: createEmptyFiltersModel()[groupId] };
}

function sortFilterValue<Value>(value: Value): Value {
  return Array.isArray(value) ? ([...value].sort() as Value) : value;
}

export function isFilterGroupChanged(first: FiltersModel, second: FiltersModel, groupId: FilterGroupId): boolean {
  return !isEqual(sortFilterValue(first[groupId]), sortFilterValue(second[groupId]));
}

export function isFiltersModelChanged(first: FiltersModel, second: FiltersModel): boolean {
  return Object.values(FilterGroupId).some((groupId) => isFilterGroupChanged(first, second, groupId));
}

export function removeFilter(filtersModel: FiltersModel, groupId: FilterGroupId, key: string): FiltersModel {
  const group = findGroup(groupId);

  if (group?.control !== 'chips' && group?.control !== 'checkboxes') return clearFilterGroup(filtersModel, groupId);

  return { ...filtersModel, [group.id]: withoutKey(filtersModel[group.id], key) };
}
